import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { StripeElementsOptions } from '@stripe/stripe-js'
import Button from './button'
import { FormEvent, useCallback, useMemo, useState, type JSX } from 'react'
import { useTranslation } from 'react-i18next'
import Loader from './loader'
import { PaymentIntent, PaymentService } from '../services/payments'
import { usePaymentIntent } from '../hooks/payments'
import { Market } from '../types/booking'
import { getStripe } from '../utils/stripe'
import { useLocale } from '../hooks/locale'

export interface StripePaymentFormOptions {
  market: Market
  redirectUri: string
  getIntent: (paymentMethodId: string) => Promise<PaymentIntent>
  billingDetails: {
    name: string
    email: string
  }
  previousError?: string | boolean | undefined
}

function StripePaymentForm ({
  market,
  redirectUri,
  getIntent,
  billingDetails,
  previousError
}: StripePaymentFormOptions): JSX.Element {
  const { t } = useTranslation()
  const stripe = useStripe()
  const elements = useElements()
  const [checkoutReady, setCheckoutReady] = useState(false)
  const [currentMethod, setCurrentMethod] = useState('')
  const [showPreviousError, setShowPreviousError] = useState(true)
  const [currentError, setCurrentError] = useState<string | null>(null)

  const { mutateAsync, isPending } = usePaymentIntent()

  const handleSubmit = useCallback((e: FormEvent<HTMLFormElement>): void => {
    e.preventDefault()
    if (stripe === null || elements === null) {
      return
    }
    setShowPreviousError(false)
    setCurrentError(null)
    void mutateAsync({
      elements,
      stripe,
      billingDetails,
      redirectUri,
      getIntent
    }, {
      onError (error) {
        setCurrentError(error.message)
      }
    })
  }, [billingDetails, elements, getIntent, mutateAsync, redirectUri, stripe])

  const options = {
    layout: {
      type: 'tabs' as const,
      defaultCollapsed: false,
      // radios: false,
      spacedAccordionItems: false
    },
    defaultValues: {
      billingDetails: {
        name: billingDetails?.name,
        email: billingDetails?.email
      }
    },
    fields: {
      billingDetails: {
        name: 'never' as const
      }
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      {currentError != null
        ? (
            <div className='text-red-500 my-2'>
              {t('payment:generic_error')}

              <div className='italic'>{currentError}</div>
            </div>
          )
        : (showPreviousError && previousError != null && previousError !== false) && (

            <div className='text-red-500 my-2'>
              {t('payment:generic_error')}
              {typeof previousError === 'string' && <div className='italic'>{currentError}</div>}
            </div>
          )}

      {currentMethod === 'card' && market !== 'north_america' && (
        <p
          className='text-secondary-500 text-sm my-4'
        >{t('credit_card_extra_charge', 'For credit cards outside the EU a 3% transaction fee is added')}
        </p>
      )}
      <PaymentElement
        options={options} onReady={() => setCheckoutReady(true)}
        onChange={e => setCurrentMethod(e.value.type)} className='[&_iframe]:outline-hidden'
      />
      <div className='mt-8'>
        <Button disabled={isPending || !checkoutReady} type='submit' tone='primary'>{t('checkout', 'Checkout')}</Button>
      </div>
      {isPending && <Loader description={t('processing_payment', 'Processing payment')} />}
    </form>
  )
}

function StripeDisclaimer ({ market }: { market: Market }): JSX.Element | undefined {
  const { t } = useTranslation()
  if (PaymentService.showNorthAmericaDisclaimer && market === 'north_america') {
    return (
      <div className='lg:col-span-4'>
        <p
          className='mt-8 text-sm p-4 border'
          dangerouslySetInnerHTML={{ __html: '*' + t('payment:north_america_disclaimer') }}
        />
      </div>
    )
  } else {
    return undefined
  }
}

export interface StripePaymentOptions {
  clientSecret?: string
  amount: number
  redirectUri: string
  getIntent: (paymentMethodId: string) => Promise<PaymentIntent>
  market: Market
  billingDetails: {
    name: string
    email: string
  }
  previousError?: string | boolean | undefined
}

export default function StripePayment ({
  market,
  amount,
  redirectUri,
  getIntent,
  billingDetails,
  previousError
}: StripePaymentOptions): JSX.Element {
  const locale = useLocale()
  const elementOptions = useMemo((): StripeElementsOptions => {
    return {
      locale,
      amount,
      currency: market === 'north_america' ? 'usd' : 'eur',
      mode: 'payment' as const,
      paymentMethodCreation: 'manual' as const, // Contrary to the type definition allowing snake_case, this option has to be camelCase!
      payment_method_options: {
        us_bank_account: {
          verification_method: 'instant' as const
        }
      }
    }
  }, [amount, locale, market])
  const stripe = getStripe(market)
  return (
    <Elements stripe={stripe} options={elementOptions}>
      <StripePaymentForm
        redirectUri={redirectUri} getIntent={getIntent} billingDetails={billingDetails} market={market}
        previousError={previousError}
      />
      <StripeDisclaimer market={market} />
    </Elements>
  )
}
